<template>
  <div class="gesp-practice-container">
    <div class="gesp-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="page-title">GESP 答题 - {{ year }}年{{ month ? month + '月' : '' }}</h1>
      </div>
      <div class="header-right">
        <div class="timer-bar">
          <i class="el-icon-time"></i>
          <span>{{ formattedElapsed }}</span>
        </div>
        <el-tag v-if="loading" size="mini" type="info" icon="el-icon-loading">加载中...</el-tag>
        <el-tag v-else-if="error" size="mini" type="danger">{{ error }}</el-tag>
        <el-tag v-else size="mini" type="success">
          共 {{ choiceQuestions.length + judgeQuestions.length + codeQuestions.length }} 题
        </el-tag>
      </div>
    </div>

    <div class="gesp-body">
      <div class="left-panel">
        <div class="panel-title">题目总览</div>

        <div class="type-menu">
          <div
            v-for="t in questionTypes"
            :key="t.key"
            class="type-item"
            :class="{ active: activeType === t.key }"
            @click="switchType(t.key)"
          >
            <span class="type-name">{{ t.label }}</span>
            <span class="type-count">{{ t.done }}/{{ t.total }}</span>
          </div>
        </div>

        <div class="number-panel">
          <div class="number-title">
            <span>{{ activeTypeLabel }}</span>
            <span class="number-meta">
              <el-checkbox v-model="showLegend" size="mini">图例</el-checkbox>
            </span>
          </div>

          <div class="legend" v-if="showLegend">
            <div class="legend-item"><span class="dot done"></span>{{ submitted ? '答对' : '已作答' }}</div>
            <div class="legend-item"><span class="dot todo"></span>未作答</div>
            <div class="legend-item"><span class="dot wrong"></span>{{ submitted ? '答错' : '已作答' }}</div>
            <div class="legend-item" v-if="!submitted"><span class="dot answered"></span>已选择</div>
          </div>

          <div class="number-grid">
            <div
              v-for="n in activeNumbers"
              :key="n.no"
              class="num"
              :class="n.status"
              @click="selectQuestion(n.no)"
            >
              {{ n.no }}
            </div>
          </div>
        </div>

        <div style="margin-top: 16px; padding: 0 4px;">
          <el-button type="danger" size="small" style="width: 100%;" @click="submitAll">提交全部</el-button>
        </div>
      </div>

      <div class="right-panel">
        <!-- 选择题 / 判断题 -->
        <div class="question-card" v-if="activeType !== 'code'">
          <div class="q-header">
            <div class="q-title">
              <span class="q-id">第 {{ currentNo }} 题 / 共 {{ (questionTypes.find(t => t.key === activeType) || {}).total || 0 }} 题</span>
              <el-tag size="mini" type="success" v-if="activeType === 'judge'">判断题</el-tag>
              <el-tag size="mini" type="primary" v-else>单选题</el-tag>
            </div>
            <div class="q-meta">分值 {{ currentQuestion.score }} 分</div>
          </div>

          <div class="q-content" v-html="renderRichText(currentQuestion.content)"></div>

          <div v-if="currentQuestion.images && currentQuestion.images.length" class="q-images">
            <el-image
              v-for="(img, idx) in currentQuestion.images"
              :key="`${currentQuestion.id || currentNo}_${idx}`"
              :src="img"
              :preview-src-list="currentQuestion.images"
              fit="contain"
              class="q-image"
            ></el-image>
          </div>

          <div class="q-options" v-if="activeType === 'choice'">
            <el-radio-group v-model="answers.choice[currentNo]">
              <el-radio v-for="(opt, idx) in currentQuestion.options" :key="opt.key" :label="opt.key" class="opt">
                <template v-if="getOptionImage(currentQuestion, idx)">
                  <div class="opt-image-wrap">
                    <span class="opt-key">{{ opt.key }}.</span>
                    <el-image
                      :src="getOptionImage(currentQuestion, idx)"
                      :preview-src-list="[getOptionImage(currentQuestion, idx)]"
                      fit="contain"
                      class="opt-image"
                    ></el-image>
                  </div>
                </template>
                <template v-else>
                  <span class="opt-text" v-html="`${opt.key}. ${renderRichText(opt.value)}`"></span>
                </template>
              </el-radio>
            </el-radio-group>
          </div>

          <div class="q-options" v-else>
            <el-radio-group v-model="answers.judge[currentNo]">
              <el-radio label="A" class="opt">正确</el-radio>
              <el-radio label="B" class="opt">错误</el-radio>
            </el-radio-group>
          </div>

          <div class="q-result" v-if="submitted && currentResult">
            <div class="result-row">
              <span class="result-label">评测结果</span>
              <span class="result-value" :class="currentResult.isCorrect ? 'correct' : 'wrong'">
                {{ currentResult.isCorrect ? '✓ 正确' : '✗ 错误' }}
              </span>
            </div>
            <div class="result-row" v-if="!currentResult.isCorrect">
              <span class="result-label">正确答案</span>
              <span class="result-value correct">{{ currentResult.correctAnswer }}</span>
            </div>
          </div>

          <div class="q-actions">
            <el-button size="small" @click="prev">上一题</el-button>
            <el-button size="small" @click="next">下一题</el-button>
          </div>
        </div>

        <!-- 编程题列表 -->
        <div class="question-card" v-else-if="activeType === 'code' && codeQuestions.length > 0">
          <div class="q-header">
            <div class="q-title">
              <el-tag size="mini" type="warning">编程题</el-tag>
              <span style="margin-left: 8px; color: #666; font-size: 13px;">共 {{ codeQuestions.length }} 题，点击进入作答</span>
            </div>
          </div>
          <div v-for="(q, idx) in codeQuestions" :key="q.id" class="code-list-item" :class="{ completed: q.hasSubmitted }">
            <div class="code-list-left">
              <span class="code-list-no">{{ idx + 1 }}</span>
              <div class="code-list-info">
                <div class="code-list-number">{{ q.number }}</div>
                <div class="code-list-title">{{ q.title }}</div>
              </div>
            </div>
            <div class="code-list-right">
              <el-tag v-if="q.difficulty" size="mini" :type="difficultyTagType(q.difficulty)" style="margin-right: 8px;">Level {{ q.difficulty }}</el-tag>
              <el-tag v-if="q.hasSubmitted" size="mini" :type="(String(q.latestSubmitStatus||'').toUpperCase()==='AC') ? 'success' : 'danger'" style="margin-right: 8px;">
                {{ String(q.latestSubmitStatus||'').toUpperCase()==='AC' ? 'AC' : (q.latestSubmitStatus ? String(q.latestSubmitStatus).toUpperCase() : '已提交') }}
              </el-tag>
              <el-tag v-else size="mini" type="info" style="margin-right: 8px;">未作答</el-tag>
              <el-button type="primary" size="mini" @click="goToCodeDetail(q)">进入作答</el-button>
            </div>
          </div>
        </div>

        <div class="question-card" v-else-if="activeType === 'code'">
          <div style="text-align:center; color: #999; padding: 40px 0;">本次考试暂无编程题</div>
        </div>
      </div>
    </div>

  <!-- 提交结果弹窗 -->
  <el-dialog
    title="本次作答结果"
    :visible.sync="submitResultVisible"
    width="360px"
    :close-on-click-modal="false"
    center
  >
    <div v-if="submitResultData" style="text-align: center; padding: 10px 0;">
      <div style="font-size: 48px; font-weight: 700; color: #409EFF; line-height: 1.2;">
        {{ submitResultData.rightCount }}<span style="font-size: 24px; color: #999;"> / {{ submitResultData.totalAnswered }}</span>
      </div>
      <div style="color: #999; font-size: 13px; margin: 6px 0 20px;">本次已作答题目正确数</div>
      <div style="display: flex; justify-content: center; gap: 32px; margin-bottom: 16px;">
        <div style="text-align: center;">
          <div style="font-size: 22px; font-weight: 700; color: #67C23A;">{{ submitResultData.rightCount }}</div>
          <div style="font-size: 12px; color: #999;">答对</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 22px; font-weight: 700; color: #F56C6C;">{{ submitResultData.wrongCount }}</div>
          <div style="font-size: 12px; color: #999;">答错</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 22px; font-weight: 700; color: #E6A23C;">{{ submitResultData.totalQuestions - submitResultData.totalAnswered }}</div>
          <div style="font-size: 12px; color: #999;">未作答</div>
        </div>
      </div>
      <div style="background: #f5f7fa; border-radius: 8px; padding: 10px; color: #555; font-size: 14px;">
        正确率：<strong style="color: #409EFF;">{{ submitResultData.totalAnswered > 0 ? Math.round(submitResultData.rightCount / submitResultData.totalAnswered * 100) : 0 }}%</strong>
      </div>
    </div>
    <div slot="footer">
      <el-button type="primary" @click="submitResultVisible = false">查看详情</el-button>
    </div>
  </el-dialog>
  </div>
</template>

<script>
import { pagingByRepoId } from '@/api/courseQuestion'
import { pagingGespQuestions } from '@/api/gespQuestion'
import { addRecord, getRecordsByUserId } from '@/api/userQuestionRecord'
import { getLatestSubmitBatch } from '@/api/gespCodeSubmit'
import { startPastPaperExam, finishPastPaperExam, updatePastPaperRecord, listPastPaperRecords } from '@/api/pastPaperRecord'
import { userStatusAdd } from '@/api/userStatus'
import { getProgressByDate, upsertTodayPlan, getProgressPaging, updateProgressFinished, getSchemeByUserIdAndRepoId, updateSchemeFinishedIds } from '@/api/studyPlan'
import { getTokenInfo } from '@/utils/jwtUtils'
import katex from 'katex'

const PRACTICE_DRAFT_PREFIX = 'gesp_practice_draft_'
const PRACTICE_LIMIT_PREFIX = 'gesp_practice_limit_'

export default {
  name: 'GESPPractice',
  data() {
    return {
      // ...
      showLegend: true,
      activeType: 'choice',
      currentNo: 1,
      questionTypes: [
        { key: 'judge', label: '判断题', done: 0, total: 0 },
        { key: 'choice', label: '单选题', done: 0, total: 0 },
        { key: 'code', label: '编程题', done: 0, total: 0 }
      ],
      // 从数据库加载的题目
      choiceQuestions: [],
      judgeQuestions: [],
      codeQuestions: [],
      codeSubmitLatestMap: {},
      answers: {
        choice: {},
        judge: {}
      },
      numberStatus: {
        choice: {},
        judge: {}
      },
      // 路由参数
      repoId: null,
      year: '',
      month: null,
      level: null,
      currentPastPaperStatus: 0,
      loading: false,
      error: '',
      timer: null,
      elapsedSeconds: 0,
      // 题目结果记录 { questionNo: { isCorrect, score, userAnswer, correctAnswer } }
      questionResults: {},
      submitted: false,
      submitResultVisible: false,
      submitResultData: null,
      timer: null,
      examRecordId: null,
      examStartTime: null,
      draftSyncTimer: null,
      restoringPastPaperRecord: false,
      syncingPastPaperRecord: false,
      statProgressId: null
    }
  },
  beforeRouteLeave(to, from, next) {
    this.stopTimer()
    if (this.draftSyncTimer) {
      clearTimeout(this.draftSyncTimer)
      this.draftSyncTimer = null
    }
    Promise.resolve(this.syncPastPaperProgress())
      .catch(() => {})
      .finally(() => {
        this.saveDraftState()
        next()
      })
  },
  watch: {
    'answers.choice': {
      deep: true,
      handler(val) {
        Object.keys(val).forEach(no => {
          if (val[no] && !this.submitted) {
            this.$set(this.numberStatus.choice, Number(no), 'answered')
          }
        })
        if (!this.submitted) {
          const t = this.questionTypes.find(x => x.key === 'choice')
          if (t) t.done = Object.keys(val).filter(k => val[k]).length
        }
        this.saveDraftState()
        this.schedulePastPaperProgressSync()
      }
    },
    'answers.judge': {
      deep: true,
      handler(val) {
        Object.keys(val).forEach(no => {
          if (val[no] && !this.submitted) {
            this.$set(this.numberStatus.judge, Number(no), 'answered')
          }
        })
        if (!this.submitted) {
          const t = this.questionTypes.find(x => x.key === 'judge')
          if (t) t.done = Object.keys(val).filter(k => val[k]).length
        }
        this.saveDraftState()
        this.schedulePastPaperProgressSync()
      }
    }
  },
  computed: {
    formattedElapsed() {
      const total = Number(this.elapsedSeconds || 0)
      const hh = Math.floor(total / 3600)
      const mm = Math.floor((total % 3600) / 60)
      const ss = total % 60
      if (hh > 0) {
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
      }
      return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    },
    activeTypeLabel() {
      const t = this.questionTypes.find(x => x.key === this.activeType)
      return t ? t.label : ''
    },
    currentResult() {
      if (!this.submitted) return null
      const key = `${this.activeType}_${this.currentNo}`
      return this.questionResults[key] || null
    },
    activeNumbers() {
      if (this.activeType === 'code') {
        return this.codeQuestions.map((q, i) => {
          const st = (q && q.latestSubmitStatus) ? String(q.latestSubmitStatus).toUpperCase() : ''
          const hasSubmit = !!(q && q.hasSubmitted)
          const status = !hasSubmit ? 'todo' : (st === 'AC' ? 'done' : 'wrong')
          return { no: i + 1, status, title: q.title || q.number }
        })
      }
      const total = (this.questionTypes.find(x => x.key === this.activeType) || {}).total || 0
      const statusMap = this.numberStatus[this.activeType] || {}
      return Array.from({ length: total }, (_, i) => {
        const no = i + 1
        return { no, status: statusMap[no] || 'todo' }
      })
    },
    currentQuestion() {
      const questions = this.activeType === 'choice' ? this.choiceQuestions : this.judgeQuestions
      const q = questions[this.currentNo - 1]
      if (q) {
        return this.formatQuestion(q)
      }
      return this.activeType === 'choice'
        ? { code: `R?-${this.currentNo}`, score: 2, content: '暂无题目', options: [] }
        : { code: `J?-${this.currentNo}`, score: 1, content: '暂无题目' }
    }
  },
  created() {
    this.hideSidebarAndNavbar()
    this.initPage()
  },
  beforeDestroy() {
    this.stopTimer()
    if (this.draftSyncTimer) {
      clearTimeout(this.draftSyncTimer)
      this.draftSyncTimer = null
    }
    this.showSidebarAndNavbar()
  },
  methods: {
    getDraftKey() {
      return `${PRACTICE_DRAFT_PREFIX}${this.repoId || 'repo'}_${this.year || 'year'}_${this.month || 'all'}_${this.level || 'all'}`
    },
    getLimitKey() {
      const courseType = this.$route.query.courseType || (this.level ? `GESP ${this.level}` : 'GESP')
      return `${PRACTICE_LIMIT_PREFIX}${this.repoId || 'repo'}_${courseType || 'course'}`
    },
    cacheRouteLimit() {
      const limit = this.$route.query.limit
      if (!limit) return
      try {
        sessionStorage.setItem(this.getLimitKey(), String(limit))
      } catch (e) {}
    },
    getCachedRouteLimit() {
      try {
        return sessionStorage.getItem(this.getLimitKey()) || ''
      } catch (e) {
        return ''
      }
    },
    saveDraftState() {
      if (!this.year || !this.repoId) return
      const draft = {
        answers: this.answers,
        numberStatus: this.numberStatus,
        questionResults: this.questionResults,
        activeType: this.activeType,
        currentNo: this.currentNo,
        elapsedSeconds: this.elapsedSeconds,
        submitted: this.submitted,
        submitResultData: this.submitResultData,
        examRecordId: this.examRecordId,
        examStartTime: this.examStartTime,
        currentPastPaperStatus: this.currentPastPaperStatus,
        savedAt: Date.now()
      }
      try {
        sessionStorage.setItem(this.getDraftKey(), JSON.stringify(draft))
      } catch (e) {}
    },
    getDraftState() {
      if (!this.year || !this.repoId) return null
      try {
        const raw = sessionStorage.getItem(this.getDraftKey())
        return raw ? JSON.parse(raw) : null
      } catch (e) {
        return null
      }
    },
    clearDraftState() {
      if (!this.year || !this.repoId) return
      try {
        sessionStorage.removeItem(this.getDraftKey())
      } catch (e) {}
    },
    getPastPaperSourceLabel() {
      const year = String(this.year || '').trim()
      const month = String(this.month || '').trim()
      if (year && month) return `${year}年${month}月真题`
      if (year) return `${year}年真题`
      return '历年真题'
    },
    restoreDraftState(draft) {
      if (!draft || typeof draft !== 'object') return
      this.answers = draft.answers || { choice: {}, judge: {} }
      this.numberStatus = draft.numberStatus || { choice: {}, judge: {} }
      this.questionResults = draft.questionResults || {}
      this.activeType = draft.activeType || this.activeType || 'choice'
      this.currentNo = draft.currentNo || 1
      this.elapsedSeconds = Number(draft.elapsedSeconds || 0)
      this.submitted = !!draft.submitted
      this.submitResultData = draft.submitResultData || null
      this.examRecordId = draft.examRecordId || null
      this.examStartTime = draft.examStartTime || null
      this.currentPastPaperStatus = Number(draft.currentPastPaperStatus || this.currentPastPaperStatus || 0)
    },
    resetPracticeState() {
      this.answers = { choice: {}, judge: {} }
      this.numberStatus = { choice: {}, judge: {} }
      this.questionResults = {}
      this.submitted = false
      this.submitResultVisible = false
      this.submitResultData = null
      this.elapsedSeconds = 0
      this.examRecordId = null
      this.examStartTime = null
      this.activeType = 'choice'
      this.currentNo = 1
    },
    startTimer() {
      this.stopTimer()
      this.timer = setInterval(() => {
        this.elapsedSeconds += 1
      }, 1000)
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    schedulePastPaperProgressSync() {
      if (this.submitted || this.restoringPastPaperRecord || !this.year || !this.repoId) return
      if (this.draftSyncTimer) {
        clearTimeout(this.draftSyncTimer)
      }
      this.draftSyncTimer = setTimeout(() => {
        this.draftSyncTimer = null
        this.syncPastPaperProgress()
      }, 400)
    },
    flushPastPaperProgressSync() {
      if (this.draftSyncTimer) {
        clearTimeout(this.draftSyncTimer)
        this.draftSyncTimer = null
      }
      this.syncPastPaperProgress()
    },
    async ensurePastPaperExamRecord(totalCount) {
      if (this.examRecordId) {
        return {
          id: this.examRecordId,
          startTime: this.examStartTime
        }
      }
      const tokenInfo = getTokenInfo()
      const userId = tokenInfo?.id || tokenInfo?.userId || tokenInfo?.sub
      if (!userId || !this.repoId || !this.year) return null
      const startResp = await startPastPaperExam({
        userId,
        repoId: Number(this.repoId) || this.repoId,
        year: String(this.year || ''),
        totalCount: Number(totalCount || 0),
        answerDetail: JSON.stringify(this.buildAnswerDetailMap())
      })
      const examRecord = startResp?.data?.data ?? startResp?.data
      if (examRecord && examRecord.id) {
        this.examRecordId = examRecord.id
        this.examStartTime = examRecord.startTime || this.examStartTime
        return {
          id: examRecord.id,
          startTime: examRecord.startTime || this.examStartTime
        }
      }
      return null
    },
    async syncPastPaperProgress() {
      if (this.submitted || this.restoringPastPaperRecord || this.syncingPastPaperRecord) return
      try {
        const tokenInfo = getTokenInfo()
        const userId = tokenInfo?.id || tokenInfo?.userId || tokenInfo?.sub
        if (!userId || !this.repoId || !this.year) return
        const totalCount = this.judgeQuestions.length + this.choiceQuestions.length + this.codeQuestions.length
        const answeredCount = Object.keys(this.answers.judge || {}).filter(k => this.answers.judge[k]).length +
          Object.keys(this.answers.choice || {}).filter(k => this.answers.choice[k]).length +
          (this.codeQuestions || []).filter(q => q && q.hasSubmitted).length
        if (answeredCount <= 0 && !this.examRecordId) return
        this.syncingPastPaperRecord = true
        const record = await this.ensurePastPaperExamRecord(totalCount)
        if (!record || !record.id) return
        await updatePastPaperRecord({
          id: record.id,
          userId,
          repoId: Number(this.repoId) || this.repoId,
          year: String(this.year || ''),
          startTime: record.startTime || this.examStartTime,
          status: 0,
          answeredCount,
          totalCount,
          duration: Number(this.elapsedSeconds || 0),
          answerDetail: JSON.stringify(this.buildAnswerDetailMap())
        })
      } catch (e) {
        console.warn('同步历年真题作答进度失败:', e)
      } finally {
        this.syncingPastPaperRecord = false
      }
    },
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) sidebar.style.display = 'none'
      if (navbar) navbar.style.display = 'none'

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = '0'
        appMain.style.margin = '0'
      }
    },
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) sidebar.style.display = ''
      if (navbar) navbar.style.display = ''

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = ''
        appMain.style.margin = ''
      }
    },
    switchType(type) {
      this.activeType = type
      this.currentNo = 1
      this.saveDraftState()
    },
    selectQuestion(no) {
      this.currentNo = no
      this.saveDraftState()
    },
    prev() {
      if (this.currentNo <= 1) return
      this.currentNo -= 1
      this.saveDraftState()
    },
    next() {
      const total = (this.questionTypes.find(x => x.key === this.activeType) || {}).total || 0
      if (this.currentNo >= total) return
      this.currentNo += 1
      this.saveDraftState()
    },
    normalizeJudgeAnswer(val) {
      if (!val) return ''
      const v = String(val).trim().toUpperCase()
      if (v === 'A' || v === 'TRUE' || v === 'T' || v === '1' || v === '正确' || v === '对') return 'A'
      if (v === 'B' || v === 'FALSE' || v === 'F' || v === '0' || v === '错误' || v === '错') return 'B'
      return v
    },
    extractChoiceKey(val) {
      if (!val) return ''
      const m = String(val).trim().match(/^([A-Ea-e])/)
      return m ? m[1].toUpperCase() : String(val).toUpperCase().trim()
    },
    getTodayDate() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    async syncStudyProgressBatch(questionResults) {
      try {
        const tokenInfo = getTokenInfo()
        const userId = tokenInfo?.id || tokenInfo?.userId || tokenInfo?.sub
        const repoIdVal = Number(this.repoId) || this.repoId
        if (!userId || !repoIdVal) return

        const normalizedResults = Array.isArray(questionResults) ? questionResults : []
        const questionIds = normalizedResults
          .map(item => Number(item && item.questionId))
          .filter(id => !isNaN(id) && id > 0)
        if (!questionIds.length) return

        try {
          const schemeResp = await getSchemeByUserIdAndRepoId(userId, repoIdVal)
          const scheme = schemeResp?.data
          const schemeId = scheme ? (scheme.id ?? scheme.schemeId) : null
          if (schemeId) {
            const currentFinished = String(scheme?.finishedId || scheme?.finished_id || '')
            const finishedSet = new Set(currentFinished.split(/[，,;\s]+/).filter(x => x).map(Number))
            questionIds.forEach(id => finishedSet.add(id))
            const merged = Array.from(finishedSet).filter(id => !isNaN(id) && id > 0).join(',')
            await updateSchemeFinishedIds(schemeId, merged)
          }
        } catch (schemeErr) {
          console.error('同步学习方案失败:', schemeErr)
        }

        const todayStr = this.getTodayDate()
        let progressId = this.statProgressId
        if (!progressId) {
          const idRes = await getProgressByDate(userId, repoIdVal, todayStr)
          const idData = idRes?.data?.data ?? idRes?.data ?? idRes
          progressId = (typeof idData === 'number' || typeof idData === 'string') ? Number(idData) : (idData && idData.id)
        }
        if (!progressId) {
          const planRes = await upsertTodayPlan({ userId, repoId: repoIdVal, date: todayStr })
          const planData = planRes?.data?.data ?? planRes?.data ?? planRes
          progressId = planData && (planData.id || planData.ID)
        }
        this.statProgressId = progressId || this.statProgressId
        if (!progressId) return

        const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
        const pdata = page?.data?.data ?? page?.data ?? {}
        const records = Array.isArray(pdata.records) ? pdata.records : []
        const first = records && records.length ? records[0] : null
        if (!first) return

        const currentFinished = String(first.finished_id || first.finishedId || '')
        const finishedSet = new Set(currentFinished.split(/[，,;\s]+/).filter(x => x).map(Number))
        questionIds.forEach(id => finishedSet.add(id))

        const mergedFinished = Array.from(finishedSet).filter(id => !isNaN(id) && id > 0).join(',')
        const correctDelta = normalizedResults.filter(item => item && item.isCorrect).length
        const wrongDelta = normalizedResults.length - correctDelta
        const correctCount = Number(first.correct_count ?? first.correctCount ?? 0) + correctDelta
        const wrongCount = Number(first.wrong_count ?? first.wrongCount ?? 0) + wrongDelta
        const reviewDone = Number(first.review_done ?? first.reviewDone ?? 0)
        await updateProgressFinished(progressId, mergedFinished, correctCount, wrongCount, reviewDone)
      } catch (e) {
        console.error('同步学习进度失败:', e)
      }
    },
    goBack() {
      this.stopTimer()
      this.flushPastPaperProgressSync()
      this.saveDraftState()
      const limit = this.$route.query.limit || this.getCachedRouteLimit() || undefined
      this.$router.push({
        path: '/amc-past-papers',
        query: {
          repoId: this.repoId || undefined,
          level: this.level || undefined,
          courseType: this.$route.query.courseType || (this.level ? `GESP ${this.level}` : 'GESP'),
          courseTypeId: this.$route.query.courseTypeId || undefined,
          courseTypeCode: this.$route.query.courseTypeCode || undefined,
          limit
        }
      })
    },
    buildAnswerDetailMap() {
      const detail = {
        judge: {},
        choice: {},
        code: {},
        meta: {
          elapsedSeconds: Number(this.elapsedSeconds || 0),
          year: this.year ? String(this.year) : '',
          month: this.month ? String(this.month) : '',
          level: this.level ? Number(this.level) : null
        }
      }

      this.judgeQuestions.forEach((q, idx) => {
        const no = idx + 1
        const userAnswer = this.answers.judge[no]
        if (!userAnswer) return
        const existing = this.questionResults[`judge_${no}`] || {}
        const correctAnswer = existing.correctAnswer || this.normalizeJudgeAnswer(q.answer)
        const hasJudged = typeof existing.isCorrect === 'boolean'
        detail.judge[no] = {
          userAnswer,
          correctAnswer,
          isCorrect: hasJudged ? existing.isCorrect : null,
          score: existing.score != null ? existing.score : 0
        }
      })

      this.choiceQuestions.forEach((q, idx) => {
        const no = idx + 1
        const userAnswer = this.answers.choice[no]
        if (!userAnswer) return
        const existing = this.questionResults[`choice_${no}`] || {}
        const correctAnswer = existing.correctAnswer || this.extractChoiceKey(q.answer)
        const hasJudged = typeof existing.isCorrect === 'boolean'
        detail.choice[no] = {
          userAnswer,
          correctAnswer,
          isCorrect: hasJudged ? existing.isCorrect : null,
          score: existing.score != null ? existing.score : 0
        }
      })

      this.codeQuestions.forEach((q, idx) => {
        const no = idx + 1
        if (!q || !q.hasSubmitted) return
        const latest = this.codeSubmitLatestMap[String(q.id)] || this.codeSubmitLatestMap[q.id] || {}
        const status = q.latestSubmitStatus || latest.status || 'WA'
        detail.code[no] = {
          questionId: q.id,
          status,
          isCorrect: String(status).toUpperCase() === 'AC',
          passedCount: Number(latest.passedCount || 0),
          totalCount: Number(latest.totalCount || 0)
        }
      })

      return detail
    },
    // 初始化页面
    async initPage() {
      this.year = this.$route.query.year
      this.month = this.$route.query.month || null
      this.level = this.$route.query.level || null
      this.repoId = this.$route.query.repoId
      this.cacheRouteLimit()
      const isRetryMode = String(this.$route.query.retry || '') === '1'
      const draft = !isRetryMode ? this.getDraftState() : null

      if (!this.year || !this.repoId) {
        this.$message.error('缺少必要参数')
        this.goBack()
        return
      }

      if (isRetryMode) {
        this.clearDraftState()
        this.resetPracticeState()
      }

      const routeElapsed = Number(this.$route.query.elapsedSeconds)

      await this.loadQuestions()
      await this.loadCodeQuestions()
      if (!isRetryMode) {
        await this.restorePastPaperRecord()
      }
      if (draft && !isRetryMode) {
        this.restoreDraftState(draft)
      }
      if (!isRetryMode && Number.isFinite(routeElapsed) && routeElapsed >= 0) {
        this.elapsedSeconds = Math.max(Number(this.elapsedSeconds || 0), routeElapsed)
      }
      if (!this.submitted) {
        this.startTimer()
      }
    },
    async restorePastPaperRecord() {
      try {
        this.restoringPastPaperRecord = true
        const tokenInfo = getTokenInfo()
        const userId = tokenInfo?.id || tokenInfo?.userId || tokenInfo?.sub
        if (!userId || !this.repoId || !this.year) return
        const resp = await listPastPaperRecords(userId, this.repoId)
        const raw = resp?.data?.data ?? resp?.data ?? []
        const records = Array.isArray(raw) ? raw : []
        const matchedRecord = records.find(record => this.matchPastPaperRecord(record))
        if (!matchedRecord) return
        this.examRecordId = matchedRecord.id || this.examRecordId
        this.examStartTime = matchedRecord.startTime || this.examStartTime
        this.currentPastPaperStatus = Number(matchedRecord.status || 0)
        this.applyPastPaperAnswerDetail(matchedRecord)
      } catch (e) {
        console.warn('恢复历年真题考试记录失败:', e)
      } finally {
        this.restoringPastPaperRecord = false
      }
    },
    matchPastPaperRecord(record) {
      if (!record || String(record.year || '') !== String(this.year || '')) return false
      const detail = this.parseAnswerDetail(record.answerDetail || record.answer_detail)
      const meta = detail && detail.meta && typeof detail.meta === 'object' ? detail.meta : {}
      const recordMonth = meta.month != null && String(meta.month) !== '' ? String(meta.month) : ''
      const currentMonth = this.month != null && String(this.month) !== '' ? String(this.month) : ''
      const recordLevel = meta.level != null && String(meta.level) !== '' ? String(meta.level) : ''
      const currentLevel = this.level != null && String(this.level) !== '' ? String(this.level) : ''
      if (currentMonth) {
        if (!recordMonth || recordMonth !== currentMonth) return false
      } else if (recordMonth) {
        return false
      }
      if (currentLevel && recordLevel && recordLevel !== currentLevel) return false
      return true
    },
    parseAnswerDetail(answerDetail) {
      if (!answerDetail) return null
      try {
        const detail = typeof answerDetail === 'string' ? JSON.parse(answerDetail) : answerDetail
        return detail && typeof detail === 'object' ? detail : null
      } catch (e) {
        return null
      }
    },
    applyPastPaperAnswerDetail(record) {
      const detail = this.parseAnswerDetail(record.answerDetail || record.answer_detail)
      if (!detail) return
      const judgeDetail = detail.judge && typeof detail.judge === 'object' ? detail.judge : {}
      const choiceDetail = detail.choice && typeof detail.choice === 'object' ? detail.choice : {}
      const codeDetail = detail.code && typeof detail.code === 'object' ? detail.code : {}
      const meta = detail.meta && typeof detail.meta === 'object' ? detail.meta : {}

      Object.keys(judgeDetail).forEach(key => {
        const no = Number(key)
        const item = judgeDetail[key] || {}
        if (!no || !item.userAnswer) return
        this.$set(this.answers.judge, no, item.userAnswer)
        if (typeof item.isCorrect === 'boolean') {
          this.$set(this.numberStatus.judge, no, item.isCorrect ? 'done' : 'wrong')
          this.$set(this.questionResults, `judge_${no}`, {
            isCorrect: item.isCorrect,
            correctAnswer: item.correctAnswer || '',
            userAnswer: item.userAnswer,
            score: Number(item.score || 0)
          })
        } else {
          this.$set(this.numberStatus.judge, no, 'answered')
        }
      })

      Object.keys(choiceDetail).forEach(key => {
        const no = Number(key)
        const item = choiceDetail[key] || {}
        if (!no || !item.userAnswer) return
        this.$set(this.answers.choice, no, item.userAnswer)
        if (typeof item.isCorrect === 'boolean') {
          this.$set(this.numberStatus.choice, no, item.isCorrect ? 'done' : 'wrong')
          this.$set(this.questionResults, `choice_${no}`, {
            isCorrect: item.isCorrect,
            correctAnswer: item.correctAnswer || '',
            userAnswer: item.userAnswer,
            score: Number(item.score || 0)
          })
        } else {
          this.$set(this.numberStatus.choice, no, 'answered')
        }
      })

      this.codeQuestions = (this.codeQuestions || []).map((q, idx) => {
        const no = idx + 1
        const item = codeDetail[String(no)] || codeDetail[no] || null
        if (!item) return q
        const status = String(item.status || '').toUpperCase()
        const existing = this.codeSubmitLatestMap[String(q.id)] || this.codeSubmitLatestMap[q.id] || {}
        this.$set(this.codeSubmitLatestMap, String(q.id), {
          ...existing,
          status: status || existing.status || 'WA',
          passedCount: Number(item.passedCount || existing.passedCount || 0),
          totalCount: Number(item.totalCount || existing.totalCount || 0)
        })
        return {
          ...q,
          hasSubmitted: true,
          latestSubmitStatus: status || q.latestSubmitStatus || 'WA'
        }
      })

      const judgeType = this.questionTypes.find(t => t.key === 'judge')
      const choiceType = this.questionTypes.find(t => t.key === 'choice')
      const codeType = this.questionTypes.find(t => t.key === 'code')
      if (judgeType) judgeType.done = Object.keys(this.numberStatus.judge || {}).filter(k => this.numberStatus.judge[k]).length
      if (choiceType) choiceType.done = Object.keys(this.numberStatus.choice || {}).filter(k => this.numberStatus.choice[k]).length
      if (codeType) codeType.done = (this.codeQuestions || []).filter(q => String(q.latestSubmitStatus || '').toUpperCase() === 'AC').length

      if (Number.isFinite(Number(meta.elapsedSeconds)) && Number(meta.elapsedSeconds) > 0) {
        this.elapsedSeconds = Number(meta.elapsedSeconds)
      } else if (Number.isFinite(Number(record.duration)) && Number(record.duration) > 0) {
        this.elapsedSeconds = Number(record.duration)
      }

      if (Number(record.status) === 1) {
        this.submitted = true
        this.currentPastPaperStatus = 1
      }
    },
    // 加载题目
    async loadQuestions() {
      this.loading = true
      this.error = null

      try {
        // 有月份时不传 year（GESP 题目 year 字段可能为 null），靠 number 字段过滤
        const resp = await pagingByRepoId(this.repoId, {
          pageNum: 1,
          pageSize: 1000,
          year: this.month ? undefined : String(this.year)
        })

        const raw = resp?.data?.data ?? resp?.data ?? {}
        let records = Array.isArray(raw?.records) ? raw.records : (Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw) ? raw : []))

        // 按月份过滤（GESP number 格式：GESP-202403-T-01）
        if (this.month) {
          const mm = String(this.month).padStart(2, '0')
          const yearStr = String(this.year)
          records = records.filter(q => {
            const num = String(q.number || '')
            return num.includes(yearStr + mm) || new RegExp(yearStr + '[-_/]0?' + this.month + '(?!\\d)').test(num)
          })
        }

        // 按题目类型分类（通过选项内容判断）
        this.choiceQuestions = []
        this.judgeQuestions = []

        records.forEach(q => {
          const options = q.optionsCn || q.options_cn || q.optionsEn || q.options_en || q.options || q.optionsStr || ''
          const optionsStr = String(options).toLowerCase()

          // 判断题特征：只有"正确/错误"或"对/错"两个选项
          const isJudge = optionsStr.includes('正确') && optionsStr.includes('错误') ||
                         optionsStr.includes('对') && optionsStr.includes('错') ||
                         /\(a\)[^\(]*正确[^\(]*\(b\)[^\(]*错误/i.test(optionsStr) ||
                         /\(a\)[^\(]*错误[^\(]*\(b\)[^\(]*正确/i.test(optionsStr)

          if (isJudge) {
            this.judgeQuestions.push(q)
          } else {
            this.choiceQuestions.push(q)
          }
        })

        // 更新题目类型统计
        const judgeType = this.questionTypes.find(t => t.key === 'judge')
        const choiceType = this.questionTypes.find(t => t.key === 'choice')

        if (judgeType) judgeType.total = this.judgeQuestions.length
        if (choiceType) choiceType.total = this.choiceQuestions.length

        // 初始化答案数组
        this.answers.choice = {}
        this.answers.judge = {}
        this.numberStatus.choice = {}
        this.numberStatus.judge = {}

        if (this.choiceQuestions.length === 0 && this.judgeQuestions.length === 0) {
          this.$message.warning('该年份暂无题目')
        }

        // 恢复用户做题记录
        try {
          const tokenInfo = getTokenInfo()
          const userId = tokenInfo?.id || tokenInfo?.userId || tokenInfo?.sub
          if (userId) {
            const recResp = await getRecordsByUserId(userId, this.repoId)
            const recRaw = recResp?.data?.data ?? recResp?.data ?? []
            const userRecords = Array.isArray(recRaw) ? recRaw : (Array.isArray(recRaw?.records) ? recRaw.records : [])

            const recordMap = {}
            userRecords.forEach(r => {
              const qid = r.quesId || r.ques_id
              if (qid) recordMap[qid] = r
            })

            // 检查是否存在已判断的记录（isRight=0/1），如有则恢复 submitted 状态
            let hasJudged = false

            // 恢复判断题
            this.judgeQuestions.forEach((q, idx) => {
              const rec = recordMap[q.id]
              if (!rec) return
              const no = idx + 1
              const ua = rec.userAnswer || rec.user_answer || ''
              const isRight = rec.isRight ?? rec.is_right ?? -1
              this.$set(this.answers.judge, no, ua)
              if (isRight === -1 || isRight === null) {
                this.$set(this.numberStatus.judge, no, 'answered')
              } else {
                hasJudged = true
                const isCorrect = isRight === 1
                const correctAnswer = this.normalizeJudgeAnswer(q.answer)
                this.$set(this.numberStatus.judge, no, isCorrect ? 'done' : 'wrong')
                this.$set(this.questionResults, `judge_${no}`, {
                  isCorrect, correctAnswer, userAnswer: ua, score: isCorrect ? (q.score || 1) : 0
                })
              }
            })

            // 恢复选择题
            this.choiceQuestions.forEach((q, idx) => {
              const rec = recordMap[q.id]
              if (!rec) return
              const no = idx + 1
              const ua = rec.userAnswer || rec.user_answer || ''
              const isRight = rec.isRight ?? rec.is_right ?? -1
              this.$set(this.answers.choice, no, ua)
              if (isRight === -1 || isRight === null) {
                this.$set(this.numberStatus.choice, no, 'answered')
              } else {
                hasJudged = true
                const isCorrect = isRight === 1
                const correctAnswer = this.extractChoiceKey(q.answer)
                this.$set(this.numberStatus.choice, no, isCorrect ? 'done' : 'wrong')
                this.$set(this.questionResults, `choice_${no}`, {
                  isCorrect, correctAnswer, userAnswer: ua, score: isCorrect ? (q.score || 2) : 0
                })
              }
            })

            if (hasJudged && !this.getDraftState() && Number(this.currentPastPaperStatus) === 1) this.submitted = true

            // 更新已答题计数
            if (judgeType) judgeType.done = Object.keys(this.numberStatus.judge || {}).filter(k => this.numberStatus.judge[k]).length
            if (choiceType) choiceType.done = Object.keys(this.numberStatus.choice || {}).filter(k => this.numberStatus.choice[k]).length
          }
        } catch (recErr) {
          console.error('恢复做题记录失败:', recErr)
        }
      } catch (e) {
        console.error('加载题目失败:', e)
        this.error = '加载题目失败，请稍后重试'
        this.$message.error('加载题目失败')
      } finally {
        this.loading = false
      }
    },
    // 格式化题目数据
    formatQuestion(q) {
      console.log('[GESP判题调试] id:', q.id, 'answer原始值:', JSON.stringify(q.answer), 'type:', typeof q.answer)
      const content = q.contentCn || q.content_cn || q.contentEn || q.content_en || q.content || ''
      const code = q.code || q.questionCode || `Q${q.id || ''}`
      const score = q.score || 2
      const images = this.extractQuestionImages(q)

      // 解析选项
      let options = []
      if (this.activeType === 'choice') {
        const optionsData = q.optionsCn || q.options_cn || q.optionsEn || q.options_en || q.options || q.optionsStr || ''
        options = this.parseOptions(optionsData)
        const optionImages = this.extractOptionImages(q)
        if (optionImages.length > options.length) {
          const keys = ['A', 'B', 'C', 'D', 'E']
          options = keys.slice(0, optionImages.length).map((key, index) => {
            const existing = options.find(item => item.key === key)
            return existing || { key, value: '' }
          })
        }
      }

      return {
        id: q.id,
        code,
        score,
        content,
        images,
        options,
        optionImages: this.extractOptionImages(q),
        answer: q.answer,
        analysis: q.analysisCn || q.analysis_cn || q.analysisEn || q.analysis_en || q.analysis || ''
      }
    },
    submitAll() {
      const judgeAnswered = Object.keys(this.answers.judge || {}).filter(k => this.answers.judge[k]).length
      const choiceAnswered = Object.keys(this.answers.choice || {}).filter(k => this.answers.choice[k]).length
      const codeAnswered = (this.codeQuestions || []).filter(q => q && q.hasSubmitted).length
      const totalAnswered = judgeAnswered + choiceAnswered + codeAnswered
      const totalQuestions = this.judgeQuestions.length + this.choiceQuestions.length + this.codeQuestions.length
      const objectiveQuestions = this.judgeQuestions.length + this.choiceQuestions.length

      if (totalAnswered === 0) {
        this.$message.warning('您还没有作答任何题目')
        return
      }

      const unanswered = totalQuestions - totalAnswered
      const confirmMsg = unanswered > 0
        ? `您还有 ${unanswered} 道题未作答，确定要提交吗？`
        : '确定提交全部答题结果吗？'

      this.$confirm(confirmMsg, '提交确认', {
        confirmButtonText: '确定提交',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const tokenInfo = getTokenInfo()
          const userId = tokenInfo?.id || tokenInfo?.userId || tokenInfo?.sub
          if (!userId) {
            this.$message.error('无法获取用户信息，请重新登录')
            return
          }

          let rightCount = 0
          let wrongCount = 0
          const now = new Date().toISOString().replace('T', ' ').substring(0, 19)
          const recordPromises = []
          const answerDetailMap = this.buildAnswerDetailMap()
          const studyResults = []

          // 批量判断判断题
          this.judgeQuestions.forEach((q, idx) => {
            const no = idx + 1
            const userAnswer = this.answers.judge[no]
            if (!userAnswer) return
            const correctAnswer = this.normalizeJudgeAnswer(q.answer)
            const normalizedUser = this.normalizeJudgeAnswer(userAnswer)
            const isCorrect = normalizedUser === correctAnswer
            isCorrect ? rightCount++ : wrongCount++
            const resultEntry = { isCorrect, correctAnswer, userAnswer, score: isCorrect ? 5 : 0 }
            this.$set(this.questionResults, `judge_${no}`, resultEntry)
            this.$set(this.numberStatus.judge, no, isCorrect ? 'done' : 'wrong')
            answerDetailMap.judge[no] = resultEntry
            studyResults.push({ questionId: q.id, isCorrect })
            recordPromises.push(addRecord({
              userId,
              quesId: q.id,
              quescontent_cn: q.contentCn || q.content_cn || q.content || '',
              quescontent_en: q.contentEn || q.content_en || '',
              duration: '0',
              userAnswer: String(userAnswer),
              answer: q.answer || '',
              judge: q.analysisCn || q.analysis_cn || '',
              mynote: '',
              isReview: 0,
              knowledge: q.knowledge || '',
              isRight: isCorrect ? 1 : 0,
              repoId: Number(this.repoId) || this.repoId,
              time: now
            }).catch(() => {}))
          })

          // 批量判断选择题
          this.choiceQuestions.forEach((q, idx) => {
            const no = idx + 1
            const userAnswer = this.answers.choice[no]
            if (!userAnswer) return
            const correctAnswer = this.extractChoiceKey(q.answer)
            const normalizedUser = this.extractChoiceKey(userAnswer)
            const isCorrect = normalizedUser === correctAnswer
            isCorrect ? rightCount++ : wrongCount++
            const resultEntry = { isCorrect, correctAnswer, userAnswer, score: isCorrect ? 5 : 0 }
            this.$set(this.questionResults, `choice_${no}`, resultEntry)
            this.$set(this.numberStatus.choice, no, isCorrect ? 'done' : 'wrong')
            answerDetailMap.choice[no] = resultEntry
            studyResults.push({ questionId: q.id, isCorrect })
            recordPromises.push(addRecord({
              userId,
              quesId: q.id,
              quescontent_cn: q.contentCn || q.content_cn || q.content || '',
              quescontent_en: q.contentEn || q.content_en || '',
              duration: '0',
              userAnswer: String(userAnswer),
              answer: q.answer || '',
              judge: q.analysisCn || q.analysis_cn || '',
              mynote: '',
              isReview: 0,
              knowledge: q.knowledge || '',
              isRight: isCorrect ? 1 : 0,
              repoId: Number(this.repoId) || this.repoId,
              time: now
            }).catch(() => {}))
          })

          // 更新类型菜单计数
          const judgeType = this.questionTypes.find(t => t.key === 'judge')
          const choiceType = this.questionTypes.find(t => t.key === 'choice')
          const codeType = this.questionTypes.find(t => t.key === 'code')
          if (judgeType) judgeType.done = Object.values(this.numberStatus.judge || {}).filter(s => s === 'done' || s === 'wrong').length
          if (choiceType) choiceType.done = Object.values(this.numberStatus.choice || {}).filter(s => s === 'done' || s === 'wrong').length
          if (codeType) codeType.done = (this.codeQuestions || []).filter(q => q && q.hasSubmitted).length

          this.submitted = true
          this.currentPastPaperStatus = 1
          this.stopTimer()

          const codeCorrectCount = Object.values(answerDetailMap.code || {}).filter(item => item && item.isCorrect === true).length
          const codePassedCount = Object.values(answerDetailMap.code || {}).reduce((sum, item) => sum + (Number(item && item.passedCount) || 0), 0)
          const objectiveCorrectCount = rightCount
          const totalCorrectCount = objectiveCorrectCount + codeCorrectCount
          const score = (objectiveCorrectCount * 5) + (codePassedCount * 5)
          const totalWrongCount = wrongCount + Math.max(codeAnswered - codeCorrectCount, 0)

          // 写入试卷记录
          try {
            let recordId = this.examRecordId
            let startTime = this.examStartTime
            if (!recordId) {
              const startResp = await startPastPaperExam({
                userId,
                repoId: Number(this.repoId) || this.repoId,
                year: String(this.year || ''),
                totalCount: totalQuestions
              })
              const examRecord = startResp?.data?.data ?? startResp?.data
              if (examRecord && examRecord.id) {
                recordId = examRecord.id
                startTime = examRecord.startTime
                this.examRecordId = recordId
                this.examStartTime = startTime
              }
            }
            if (recordId) {
              await finishPastPaperExam({
                id: recordId,
                userId,
                repoId: Number(this.repoId) || this.repoId,
                year: String(this.year || ''),
                startTime,
                answeredCount: totalAnswered,
                correctCount: totalCorrectCount,
                wrongCount: totalWrongCount,
                score,
                scoreRate: totalQuestions > 0 ? Math.round(totalCorrectCount / totalQuestions * 100) : 0,
                totalCount: totalQuestions,
                duration: Number(this.elapsedSeconds || 0),
                answerDetail: JSON.stringify(answerDetailMap)
              })
            }
          } catch (e) {
            console.error('写入试卷记录失败:', e)
          }

          await Promise.all(recordPromises)

          await this.syncStudyProgressBatch(studyResults)

          try {
            const statusQuestionList = [
              ...this.judgeQuestions.map(q => q && q.id).filter(Boolean),
              ...this.choiceQuestions.map(q => q && q.id).filter(Boolean),
              ...this.codeQuestions.map(q => {
                if (!q) return null
                return q.number || q.questionNumber || q.displayNumber || `code:${q.id}`
              }).filter(Boolean)
            ]
            const quesList = statusQuestionList.join(';')
            if (quesList) {
              await userStatusAdd({
                userId,
                quesList,
                count: statusQuestionList.length,
                rightCount: totalCorrectCount,
                source: this.getPastPaperSourceLabel(),
                repoId: Number(this.repoId) || this.repoId
              })
            }
          } catch (e) {
            console.warn('写入用户状态失败:', e)
          }

          this.clearDraftState()

          // 弹出本次统计
          this.submitResultData = {
            totalAnswered,
            rightCount: totalCorrectCount,
            wrongCount: totalWrongCount,
            totalQuestions
          }
          this.submitResultVisible = true
        } catch (e) {
          console.error('提交全部失败:', e)
          this.$message.error('提交失败，请稍后重试')
        }
      }).catch(() => {})
    },
    // 加载编程题（编程题库 repoId 固定为 25，后端按 year/month/level 从 number 字段过滤）
    async loadCodeQuestions() {
      try {
        const params = {
          pageNum: 1,
          pageSize: 200,
          repoId: 27,
          year: this.year ? String(this.year) : undefined,
          month: this.month ? String(this.month) : undefined,
          level: this.level ? Number(this.level) : undefined
        }
        const resp = await pagingGespQuestions(params)
        const raw = resp?.data?.data ?? resp?.data ?? {}
        const records = Array.isArray(raw?.records) ? raw.records
          : (Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw) ? raw : []))

        this.codeQuestions = records
        const codeType = this.questionTypes.find(t => t.key === 'code')
        if (codeType) codeType.total = records.length

        await this.loadCodeLatestSubmitStatus(records)

        // 如果没有判断题/选择题但有编程题，默认切到编程题
        if (records.length > 0 && this.choiceQuestions.length === 0 && this.judgeQuestions.length === 0) {
          this.activeType = 'code'
        }
      } catch (e) {
        console.error('加载编程题失败:', e)
      }
    },
    async loadCodeLatestSubmitStatus(records) {
      try {
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id
        if (!userId) return
        const ids = Array.isArray(records) ? records.map(r => r && r.id).filter(Boolean) : []
        if (ids.length === 0) return

        const resp = await getLatestSubmitBatch(userId, ids)
        const map = resp?.data?.data ?? resp?.data ?? {}
        this.codeSubmitLatestMap = map || {}

        this.codeQuestions = (this.codeQuestions || []).map(q => {
          const key = String(q.id)
          const latest = map ? (map[key] || map[q.id]) : null
          const status = latest && latest.status ? String(latest.status).toUpperCase() : ''
          const hasSubmitted = !!latest
          return { ...q, hasSubmitted, latestSubmitStatus: status }
        })

        const codeType = this.questionTypes.find(t => t.key === 'code')
        if (codeType) {
          codeType.done = (this.codeQuestions || []).filter(q => String(q.latestSubmitStatus || '').toUpperCase() === 'AC').length
        }
      } catch (e) {
        console.error('加载编程题提交状态失败:', e)
      }
    },
    goToCodeDetail(q) {
      this.saveDraftState()
      this.$router.push({
        path: '/gesp-code-detail',
        query: {
          questionId: q.id,
          year: this.year,
          month: this.month || undefined,
          level: this.level || undefined,
          repoId: this.repoId || undefined,
          source: 'gesp-practice',
          elapsedSeconds: this.elapsedSeconds || 0
        }
      })
    },
    difficultyTagType(level) {
      const map = { 1: 'success', 2: 'success', 3: '', 4: '', 5: 'warning', 6: 'danger' }
      return map[level] || ''
    },
    // 解析选项
    parseOptions(raw) {
      if (!raw) return []

      // 如果已经是数组
      if (Array.isArray(raw)) {
        return raw.map(item => {
          if (typeof item === 'string') {
            const m = item.match(/^\s*\(?([A-Ea-e])\)?[\.\)]?\s*(.*)$/)
            return m ? { key: m[1].toUpperCase(), value: m[2].trim() } : { key: '', value: item }
          }
          if (item && typeof item === 'object') {
            const k = (item.key || item.label || '').toString().toUpperCase()
            const v = item.value != null ? String(item.value) : (item.text || '')
            return { key: k, value: (v || '').trim() }
          }
          return { key: '', value: String(item) }
        }).filter(o => o.key)
      }

      // 如果是对象 {A:'...', B:'...'}
      if (typeof raw === 'object') {
        return Object.keys(raw).map(k => ({ key: k.toUpperCase(), value: String(raw[k] || '').trim() }))
      }

      // 如果是字符串
      const optionsStr = String(raw)
      const options = []

      // 解析 (A) xxx; (B) yyy; ...
      const reParen = /\(([A-Ea-e])\)\s*([^;\n\r]+)/g
      let m
      while ((m = reParen.exec(optionsStr)) !== null) {
        options.push({ key: m[1].toUpperCase(), value: m[2].trim() })
      }
      if (options.length) return options

      // 解析 A. xxx 或 A) xxx
      const parts = optionsStr.split(/\n|;|<br\s*\/>|<br>/i).map(s => s.trim()).filter(Boolean)
      for (const p of parts) {
        const m2 = p.match(/^\s*\(?([A-Ea-e])\)?\s*[\.\)]?\s*(.*)$/)
        if (m2) options.push({ key: m2[1].toUpperCase(), value: m2[2].trim() })
      }
      return options
    },
    extractQuestionImages(q) {
      if (!q || typeof q !== 'object') return []
      const sources = [q.imagePaths, q.image_paths, q.image, q.images]
      const images = []
      sources.forEach(src => {
        this.parseImageField(src).forEach(item => {
          const url = this.resolveImageUrl(item)
          if (url && !images.includes(url)) {
            images.push(url)
          }
        })
      })
      return images
    },
    extractOptionImages(q) {
      if (!q || typeof q !== 'object') return []
      const sources = [q.optionImages, q.option_images]
      const images = []
      sources.forEach(src => {
        this.parseImageField(src).forEach(item => {
          const url = this.resolveImageUrl(item)
          if (url) {
            images.push(url)
          }
        })
      })
      return images
    },
    getOptionImage(question, optionIndex) {
      if (!question || !Array.isArray(question.optionImages)) return ''
      return question.optionImages[optionIndex] || ''
    },
    decodeEscapedNewlines(text) {
      if (text == null) return ''
      return String(text)
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\n')
    },
    normalizeLatexInput(text) {
      if (text == null) return ''
      return String(text)
        .replace(/&nbsp;/gi, ' ')
        .replace(/(^|[^\\])%/g, '$1\\%')
        .replace(/(^|[^\\])times\b/g, '$1\\times')
        .replace(/(^|[^\\])cdot\b/g, '$1\\cdot')
    },
    escapeHtml(text) {
      if (text == null) return ''
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },
    decodeHtmlEntities(text) {
      if (text == null) return ''
      return String(text)
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
    },
    renderKatex(expr, displayMode) {
      try {
        let formula = this.decodeHtmlEntities(this.normalizeLatexInput(String(expr || '').trim()))
        if (window && typeof window.fixLatexNotation === 'function') {
          formula = window.fixLatexNotation(formula)
        }
        return katex.renderToString(formula, {
          throwOnError: false,
          displayMode: !!displayMode,
          strict: false,
          trust: true
        })
      } catch (e) {
        return this.escapeHtml(displayMode ? `\\[${expr}\\]` : `\\(${expr}\\)`)
      }
    },
    renderRichText(text) {
      const decoded = this.decodeEscapedNewlines(text)
      const input = this.escapeHtml(this.normalizeLatexInput(decoded)
        .replace(/\r\n/g, '\n')
        .replace(/\\\\\(/g, '\\(')
        .replace(/\\\\\)/g, '\\)')
        .replace(/\\\\\[/g, '\\[')
        .replace(/\\\\\]/g, '\\]'))
      if (katex && typeof katex.renderToString === 'function') {
        let html = input
        html = html.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr) => this.renderKatex(expr, true))
        html = html.replace(/\$\$([\s\S]+?)\$\$/g, (_, expr) => this.renderKatex(expr, true))
        html = html.replace(/\\\(([\s\S]+?)\\\)/g, (_, expr) => this.renderKatex(expr, false))
        html = html.replace(/\$([^$\n]+?)\$/g, (_, expr) => this.renderKatex(expr, false))
        return html.replace(/\n/g, '<br>')
      }
      return input.replace(/\n/g, '<br>')
    },
    parseImageField(raw) {
      if (!raw) return []
      if (Array.isArray(raw)) {
        return raw.map(item => String(item || '').trim()).filter(Boolean)
      }
      if (typeof raw === 'object') {
        return Object.values(raw).map(item => String(item || '').trim()).filter(Boolean)
      }
      const str = String(raw).trim()
      if (!str) return []
      try {
        const parsed = JSON.parse(str)
        if (Array.isArray(parsed)) {
          return parsed.map(item => String(item || '').trim()).filter(Boolean)
        }
      } catch (e) {}
      return str.split(/[;,]/).map(item => String(item || '').trim()).filter(Boolean)
    },
    resolveImageUrl(path) {
      if (!path) return ''
      const s = String(path).trim()
      if (!s) return ''
      if (/^(https?:)?\/\//i.test(s) || s.startsWith('data:')) return s
      if (s.startsWith('/api/')) return s
      if (/^\/[A-Za-z0-9_-]+\//.test(s)) {
        return `/api/file/preview?path=${encodeURIComponent(s)}`
      }
      const minioBase = (process.env.VUE_APP_MINIO_URL || '').replace(/\/$/, '')
      if (minioBase) {
        return s.startsWith('/') ? `${minioBase}${s}` : `${minioBase}/${s}`
      }
      return s
    }
  }
}
</script>

<style scoped>
.gesp-practice-container { min-height: 100vh; background: #f5f7fa; display: flex; flex-direction: column; }
.gesp-header { background: #fff; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 8px rgba(0,0,0,0.06); position: sticky; top: 0; z-index: 10; }
.header-left { display: flex; align-items: center; gap: 10px; }
.header-right { display: flex; align-items: center; gap: 10px; }
.back-btn { font-size: 18px; color: #666; cursor: pointer; padding: 4px; border-radius: 50%; }
.back-btn:hover { background: #f5f5f5; }
.page-title { margin: 0; font-size: 16px; font-weight: 600; }
.timer-bar { display: flex; align-items: center; gap: 6px; color: #409EFF; font-weight: 600; }

.gesp-body { display: flex; gap: 12px; padding: 12px; flex: 1; min-height: 0; align-items: stretch; }
.left-panel { width: 300px; background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 12px; display: flex; flex-direction: column; }
.right-panel { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 12px; }
.panel-title { font-weight: 600; color: #333; margin-bottom: 10px; }

.type-menu { display: flex; flex-direction: column; gap: 6px; }
.type-item { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; border-radius: 8px; cursor: pointer; color: #333; background: #f8fafc; border: 1px solid #eef2f7; }
.type-item:hover { background: #f3f6fb; }
.type-item.active { border-color: #409EFF; background: #ecf5ff; }
.type-name { font-weight: 600; }
.type-count { color: #666; font-size: 12px; }

.number-panel { margin-top: 12px; }
.number-title { display: flex; justify-content: space-between; align-items: center; font-weight: 600; color: #333; margin-bottom: 8px; }
.legend { display: flex; flex-wrap: wrap; gap: 8px 12px; margin-bottom: 10px; color: #666; font-size: 12px; }
.legend-item { display: flex; align-items: center; gap: 6px; }
.dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.dot.done { background: #67C23A; }
.dot.todo { background: #C0C4CC; }
.dot.wrong { background: #F56C6C; }
.dot.answered { background: #409EFF; }

.number-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
.num { height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; cursor: pointer; border: 1px solid #e5e7eb; color: #333; background: #fff; font-size: 13px; }
.num.todo { background: #fff; }
.num.done { background: #f0f9eb; border-color: #67c23a; color: #2f7a1f; }
.num.wrong { background: #fef0f0; border-color: #f8d4d4; color: #b33a3a; }
.num.answered { background: #f0f9eb; border-color: #67c23a; color: #2f7a1f; }

.question-card { background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 24px 32px; flex: 1; display: flex; flex-direction: column; }
.q-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 1px solid #f0f0f0; }
.q-title { display: flex; align-items: center; gap: 8px; }
.q-id { font-weight: 700; color: #333; font-size: 16px; }
.q-meta { color: #666; font-size: 13px; }
.q-content { line-height: 1.8; color: #333; margin-bottom: 24px; font-size: 16px; }
.q-images { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.q-image { width: 100%; max-width: 520px; border-radius: 8px; border: 1px solid #ebeef5; background: #fff; }
.q-options { margin-bottom: 24px; font-size: 15px; }
.opt { display: block; margin: 10px 0; padding: 4px 0; }
.opt-image-wrap { display: flex; align-items: flex-start; gap: 8px; }
.opt-key { min-width: 20px; font-weight: 600; color: #333; line-height: 24px; }
.opt-image { width: 100%; max-width: 320px; max-height: 240px; border-radius: 8px; border: 1px solid #ebeef5; background: #fff; }
.q-actions { display: flex; gap: 10px; margin-top: 24px; padding-top: 16px; border-top: 1px solid #f0f0f0; }

.q-result { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 8px; padding: 14px; margin-top: 20px; }
.result-row { display: flex; justify-content: space-between; padding: 6px 0; }
.result-label { color: #666; font-weight: 500; }
.result-value { font-weight: 600; }
.result-value.correct { color: #67C23A; }
.result-value.wrong { color: #F56C6C; }
.result-value.placeholder { color: #999; }

.tip-card { background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 12px; color: #666; line-height: 1.7; }

.q-section-title { font-weight: 600; color: #555; font-size: 13px; margin-bottom: 4px; padding-left: 6px; border-left: 3px solid #409EFF; }
.q-desc { color: #444; line-height: 1.7; font-size: 14px; padding: 4px 0; }
.q-pre { background: #f5f7fa; border: 1px solid #e4e7ed; border-radius: 6px; padding: 10px 12px; font-family: 'Courier New', monospace; font-size: 13px; line-height: 1.6; white-space: pre-wrap; word-break: break-all; margin: 0; }

.code-list-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; border: 1px solid #eef2f7; border-radius: 8px; margin-top: 10px; background: #fafbfc; }
.code-list-item.completed { border-color: #67c23a; }
.code-list-item:hover { background: #f0f7ff; border-color: #c6e2ff; }
.code-list-item.completed:hover { border-color: #67c23a; }
.code-list-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.code-list-no { width: 28px; height: 28px; border-radius: 50%; background: #409EFF; color: #fff; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.code-list-item.completed .code-list-no { background: #67c23a; }
.code-list-info { min-width: 0; }
.code-list-number { font-size: 12px; color: #999; }
.code-list-title { font-weight: 600; color: #333; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.code-list-right { display: flex; align-items: center; flex-shrink: 0; }
</style>
